import React, { useContext, useState, useEffect } from 'react';
import { AuthContext } from 'react-oauth2-code-pkce';
import GlobalContext from '../context/GlobalContext';
import UsersService from '../services/users-service';
import CompaniesService from '../services/companies-service';
import AdminShell from './AdminShell';
import DeniedCard from './DeniedCard';

export default function UserListPage() {
  const { currentUser, userLoading } = useContext(GlobalContext);
  const { token } = useContext(AuthContext);
  
  const [usersPage, setUsersPage] = useState({ content: [], totalElements: 0, totalPages: 0, number: 0, size: 20 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [sortBy, setSortBy] = useState('email');
  const [sortOrder, setSortOrder] = useState('asc');
  const [searchTimeout, setSearchTimeout] = useState(null);
  
  // User creation state
  const [isCreating, setIsCreating] = useState(false);
  const [newUser, setNewUser] = useState({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    isAdmin: false,
    isReadonly: false,
    companyIds: []
  });
  const [creating, setCreating] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [successMessage, setSuccessMessage] = useState('');

  // Fetch users on component mount and when search/pagination changes
  useEffect(() => {
    if (token && currentUser?.isAdmin) {
      fetchUsers();
    }
  }, [token, currentUser, currentPage, pageSize, sortBy, sortOrder]);

  // Debounced search
  useEffect(() => {
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }
    
    const timeout = setTimeout(() => {
      if (currentPage === 0) {
        fetchUsers();
      } else {
        setCurrentPage(0); // Reset to first page, will trigger fetchUsers via other useEffect
      }
    }, 500); // 500ms delay
    
    setSearchTimeout(timeout);
    
    return () => {
      if (timeout) clearTimeout(timeout);
    };
  }, [searchTerm]);

  const fetchUsers = async () => {
    setLoading(true);
    setError(null);
    try {
      const sortParam = `${sortBy},${sortOrder}`;
      const data = await UsersService.getAllUsers(token, searchTerm, currentPage, pageSize, sortParam);
      setUsersPage(data);
    } catch (err) {
      setError('Fout bij laden van gebruikers: ' + (err.message || err));
    } finally {
      setLoading(false);
    }
  };

  // Filter and sort users - now handled server-side, but keep local sorting for immediate feedback
  const users = usersPage.content || [];
  const totalUsers = usersPage.totalElements || 0;
  const totalPages = usersPage.totalPages || 0;

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
    setCurrentPage(0); // Reset to first page when sorting changes
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handlePageSizeChange = (newSize) => {
    setPageSize(newSize);
    setCurrentPage(0); // Reset to first page when page size changes
  };

  const getSortIcon = (field) => {
    if (sortBy !== field) return '↕️';
    return sortOrder === 'asc' ? '↑' : '↓';
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }
    };
  }, [searchTimeout]);

  // Fetch companies for user creation
  useEffect(() => {
    if (token && currentUser?.isAdmin) {
      fetchCompanies();
    }
  }, [token, currentUser]);

  const fetchCompanies = async () => {
    try {
      const companiesData = await CompaniesService.getAllCompanies(token);
      setCompanies(companiesData);
    } catch (err) {
      console.error('Failed to fetch companies:', err);
    }
  };

  const handleCreateUser = async () => {
    if (!newUser.email.trim() || !newUser.password.trim()) {
      setError('Email en wachtwoord zijn verplicht');
      return;
    }

    setCreating(true);
    setError(null);
    try {
      await UsersService.createUser({
        email: newUser.email.trim(),
        password: newUser.password,
        firstName: newUser.firstName.trim() || null,
        lastName: newUser.lastName.trim() || null,
        isAdmin: newUser.isAdmin,
        isReadonly: newUser.isReadonly,
        companyIds: newUser.companyIds
      }, token);
      
      await fetchUsers();
      setIsCreating(false);
      setNewUser({ email: '', password: '', firstName: '', lastName: '', isAdmin: false, isReadonly: false, companyIds: [] });
      setSuccessMessage('Gebruiker succesvol aangemaakt!');
      setTimeout(() => setSuccessMessage(''), 5000);
    } catch (err) {
      setError('Fout bij aanmaken van gebruiker: ' + (err.message || err));
    } finally {
      setCreating(false);
    }
  };

  const handleCancelCreate = () => {
    setIsCreating(false);
    setNewUser({ email: '', password: '', firstName: '', lastName: '', isAdmin: false, isReadonly: false, companyIds: [] });
    setError(null);
  };

  const handleCompanyToggle = (companyId) => {
    setNewUser(prev => ({
      ...prev,
      companyIds: prev.companyIds.includes(companyId)
        ? prev.companyIds.filter(id => id !== companyId)
        : [...prev.companyIds, companyId]
    }));
  };

  if (userLoading) {
    return (
      <div className="page-shell flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!currentUser?.isAdmin) {
    return <DeniedCard />;
  }

  return (
    <AdminShell
      title="Gebruikersbeheer"
      backHref="/beheer"
      backLabel="Beheer"
      actions={(
        <>
          <button type="button" onClick={() => setIsCreating(true)} disabled={loading} className="btn-primary">
            Nieuwe gebruiker
          </button>
          <button type="button" onClick={fetchUsers} disabled={loading} className="btn-ghost">
            Vernieuwen
          </button>
        </>
      )}
    >
        {/* Search and filters */}
        <div className="card p-6 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Zoeken (server-side)
              </label>
              <input
                type="text"
                placeholder="Zoek op naam of email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="input-field h-auto py-2"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Sorteer op
              </label>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="input-field h-auto py-2 w-auto"
              >
                <option value="email">Email</option>
                <option value="firstName">Voornaam</option>
                <option value="lastName">Achternaam</option>
                <option value="createdTimestamp">Aangemaakt</option>
                <option value="enabled">Status</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Per pagina
              </label>
              <select
                value={pageSize}
                onChange={(e) => handlePageSizeChange(parseInt(e.target.value))}
                className="input-field h-auto py-2 w-auto"
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>
          </div>
          
          <div className="mt-4 flex flex-col md:flex-row justify-between items-center text-sm text-gray-600">
            <div>
              {loading ? 'Laden...' : `${totalUsers} gebruikers gevonden`}
              {searchTerm && ` voor "${searchTerm}"`}
            </div>
            {totalPages > 1 && (
              <div className="flex items-center gap-2 mt-2 md:mt-0">
                <span>Pagina {currentPage + 1} van {totalPages}</span>
              </div>
            )}
          </div>
        </div>

        {/* Error message */}
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
            {error}
          </div>
        )}

        {/* Success message */}
        {successMessage && (
          <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-6">
            {successMessage}
          </div>
        )}

        {/* Users table */}
        <div className="card overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <span className="ml-3 text-gray-600">Gebruikers laden...</span>
            </div>
          ) : users.length === 0 ? (
            <div className="text-center py-12">
              <span className="material-icons-outlined text-gray-400 text-6xl mb-4">person_off</span>
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">Geen gebruikers gevonden</h3>
              <p className="text-gray-600">
                {searchTerm ? 'Probeer een andere zoekterm' : 'Er zijn nog geen gebruikers'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-800">
                <thead className="bg-gray-50 dark:bg-gray-800/50">
                  <tr>
                    <th 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('email')}
                    >
                      Email {getSortIcon('email')}
                    </th>
                    <th 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('firstName')}
                    >
                      Naam {getSortIcon('firstName')}
                    </th>
                    <th 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('enabled')}
                    >
                      Status {getSortIcon('enabled')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Admin
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Alleen lezen
                    </th>
                    <th 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('createdTimestamp')}
                    >
                      Aangemaakt {getSortIcon('createdTimestamp')}
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Acties
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-800">
                  {users.map((user, index) => {
                    const keycloak = user.keycloakUser || {};
                    const internal = user.internalUser || {};
                    const displayName = `${keycloak.firstName || ''} ${keycloak.lastName || ''}`.trim() || 'Onbekend';
                    const displayEmail = keycloak.email || internal.email || 'Onbekend';
                    
                    return (
                      <tr key={keycloak.id || index} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{displayEmail}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900 dark:text-gray-100">{displayName}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            keycloak.enabled 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {keycloak.enabled ? 'Actief' : 'Inactief'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            internal?.isAdmin 
                              ? 'bg-purple-100 text-purple-800' 
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            {internal?.isAdmin ? 'Ja' : 'Nee'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            internal?.isReadonly 
                              ? 'bg-orange-100 text-orange-800' 
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            {internal?.isReadonly ? 'Ja' : 'Nee'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                          {keycloak.createdTimestamp 
                            ? new Date(keycloak.createdTimestamp).toLocaleDateString('nl-NL')
                            : 'Onbekend'
                          }
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          {keycloak.id ? (
                            <a
                              href={`/gebruikers/${keycloak.id}`}
                              className="text-blue-600 hover:text-blue-900 transition-colors"
                            >
                              Details
                            </a>
                          ) : (
                            <span className="text-gray-400 text-sm">Geen ID</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="card p-4 mb-6">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-600">
                Pagina {currentPage + 1} van {totalPages} ({totalUsers} totaal)
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handlePageChange(0)}
                  disabled={currentPage === 0 || loading}
                  className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-800"
                >
                  Eerste
                </button>
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 0 || loading}
                  className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-800"
                >
                  Vorige
                </button>
                
                {/* Page numbers */}
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i;
                  } else if (currentPage <= 2) {
                    pageNum = i;
                  } else if (currentPage >= totalPages - 3) {
                    pageNum = totalPages - 5 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }
                  
                  return (
                    <button
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      disabled={loading}
                      className={`px-3 py-1 border rounded text-sm ${
                        pageNum === currentPage
                          ? 'bg-blue-600 text-white border-blue-600'
                          : 'border-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed'
                      }`}
                    >
                      {pageNum + 1}
                    </button>
                  );
                })}
                
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage >= totalPages - 1 || loading}
                  className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-800"
                >
                  Volgende
                </button>
                <button
                  onClick={() => handlePageChange(totalPages - 1)}
                  disabled={currentPage >= totalPages - 1 || loading}
                  className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-800"
                >
                  Laatste
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Summary stats */}
        {!loading && users.length > 0 && (
          <div className="mt-6 grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="card p-6">
              <div className="text-2xl font-bold text-blue-600">{totalUsers}</div>
              <div className="text-sm text-gray-600">Totaal gebruikers</div>
            </div>
            <div className="card p-6">
              <div className="text-2xl font-bold text-green-600">
                {users.filter(u => u.keycloakUser?.enabled).length}
              </div>
              <div className="text-sm text-gray-600">Actieve gebruikers (pagina)</div>
            </div>
            <div className="card p-6">
              <div className="text-2xl font-bold text-purple-600">
                {users.filter(u => u.internalUser?.isAdmin).length}
              </div>
              <div className="text-sm text-gray-600">Beheerders (pagina)</div>
            </div>
            <div className="card p-6">
              <div className="text-2xl font-bold text-orange-600">
                {users.filter(u => u.internalUser?.companies?.length > 0).length}
              </div>
              <div className="text-sm text-gray-600">Gebruikers met bedrijf (pagina)</div>
            </div>
          </div>
        )}

      {/* Create User Modal */}
      {isCreating && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-xl max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-800">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">Nieuwe gebruiker aanmaken</h3>
                <button
                  onClick={handleCancelCreate}
                  disabled={creating}
                  className="text-gray-400 hover:text-gray-500 disabled:opacity-50"
                >
                  <span className="material-icons-outlined">close</span>
                </button>
              </div>
            </div>

            <div className="px-6 py-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email *
                </label>
                <input
                  type="email"
                  value={newUser.email}
                  onChange={(e) => setNewUser(prev => ({ ...prev, email: e.target.value }))}
                  disabled={creating}
                  placeholder="Voer emailadres in..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Voornaam
                  </label>
                  <input
                    type="text"
                    value={newUser.firstName}
                    onChange={(e) => setNewUser(prev => ({ ...prev, firstName: e.target.value }))}
                    disabled={creating}
                    placeholder="Voornaam"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Achternaam
                  </label>
                  <input
                    type="text"
                    value={newUser.lastName}
                    onChange={(e) => setNewUser(prev => ({ ...prev, lastName: e.target.value }))}
                    disabled={creating}
                    placeholder="Achternaam"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Wachtwoord *
                </label>
                <input
                  type="password"
                  value={newUser.password}
                  onChange={(e) => setNewUser(prev => ({ ...prev, password: e.target.value }))}
                  disabled={creating}
                  placeholder="Voer wachtwoord in..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                />
              </div>

              <div>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={newUser.isAdmin}
                    onChange={(e) => setNewUser(prev => ({ ...prev, isAdmin: e.target.checked }))}
                    disabled={creating}
                    className="mr-3 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded disabled:opacity-50"
                  />
                  <span className="text-sm font-medium text-gray-700">Administrator</span>
                </label>
              </div>

              <div>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={newUser.isReadonly}
                    onChange={(e) => setNewUser(prev => ({ ...prev, isReadonly: e.target.checked }))}
                    disabled={creating}
                    className="mr-3 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded disabled:opacity-50"
                  />
                  <div>
                    <span className="text-sm font-medium text-gray-700">Alleen lezen</span>
                    <p className="text-xs text-gray-500">Gebruiker kan alleen gegevens bekijken, niet wijzigen</p>
                  </div>
                </label>
              </div>

              {companies.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Bedrijven
                  </label>
                  <div className="space-y-2 max-h-40 overflow-y-auto border border-gray-200 dark:border-gray-800 rounded-md p-3">
                    {companies.map(company => (
                      <label key={company.id} className="flex items-start">
                        <input
                          type="checkbox"
                          checked={newUser.companyIds.includes(company.id)}
                          onChange={() => handleCompanyToggle(company.id)}
                          disabled={creating}
                          className="mr-3 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded disabled:opacity-50 mt-0.5"
                        />
                        <div className="min-w-0 flex-1">
                          <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{company.name}</span>
                          {company.createdAt && (
                            <p className="text-xs text-gray-500">
                              Aangemaakt: {new Date(company.createdAt).toLocaleDateString('nl-NL')}
                            </p>
                          )}
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-800 flex justify-end gap-3">
              <button
                onClick={handleCancelCreate}
                disabled={creating}
                className="btn-ghost"
              >
                Annuleren
              </button>
              <button
                onClick={handleCreateUser}
                disabled={creating || !newUser.email.trim() || !newUser.password.trim()}
                className="btn-primary disabled:bg-gray-400 flex items-center"
              >
                {creating ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Aanmaken...
                  </>
                ) : (
                  <>
                    <span className="material-icons-outlined mr-2 text-sm">person_add</span>
                    Gebruiker aanmaken
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminShell>
  );
}
